import React from "react"
import {useTranslation} from "react-i18next"
import {useInView} from "react-intersection-observer"
import {MagnifyingGlassIcon, XMarkIcon} from "@heroicons/react/24/outline"
import {CalendarDaysIcon} from "@heroicons/react/24/solid"
import {format} from "date-fns/format"

import Button from "../../../../components/Button.tsx"
import Calendar from "../../../../components/Calendar.tsx"
import {InputBase} from "../../../../components/fields/Input.tsx"
import {useInfiniteSalesAssistantUpdateCenterQuery} from "../../../../queries/salesAssistant.ts"
import {useClickOutsideRef, useDebouncedValue} from "../../../../utils/hooks.tsx"
import {EMPTY_ARRAY} from "../../../../utils/index.ts"
import {UpdateCenterEntryCard} from "../../components/UpdateCenterEntryCard.tsx"
import {useSalesAssistantSalesPersonFilter} from "../../hooks.ts"

const PAGE_SIZE = 10

export const UpdateCenterSection: React.FC = () => {
  const {t} = useTranslation()
  const [searchString, setSearchString] = React.useState("")
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null)
  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false)
  const debouncedSearchString = useDebouncedValue(searchString)

  const closeCalendar = React.useCallback(() => setIsCalendarOpen(false), [])
  const calendarRef = useClickOutsideRef<HTMLDivElement>(closeCalendar)

  const dateFilter = React.useMemo(() => {
    return selectedDate ? format(selectedDate, "yyyy-MM-dd") : undefined
  }, [selectedDate])

  const handleDateSelect = React.useCallback(
    (date: Date) => {
      setSelectedDate(date)
      setIsCalendarOpen(false)
    },
    [setSelectedDate]
  )

  const handleClearDate = React.useCallback(() => {
    setSelectedDate(null)
    setIsCalendarOpen(false)
  }, [])

  const [salesPersonExternalId] = useSalesAssistantSalesPersonFilter()

  const {data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching} = useInfiniteSalesAssistantUpdateCenterQuery(
    {
      search: debouncedSearchString,
      date: dateFilter,
      salesPersonExternalId,
      pageSize: PAGE_SIZE,
    }
  )

  const allEntries = React.useMemo(() => {
    if (!data?.pages) {
      return EMPTY_ARRAY
    }
    return data.pages.flatMap(page => page.data.update_center_entries ?? EMPTY_ARRAY)
  }, [data?.pages])

  const [scrollableContainer, setScrollableContainer] = React.useState<HTMLDivElement | null>(null)
  const scrollableContainerRef = React.useCallback((node: HTMLDivElement | null) => {
    setScrollableContainer(node)
  }, [])

  const {ref: loadMoreRef} = useInView({
    root: scrollableContainer,
    rootMargin: "100px",
    skip: !hasNextPage || isFetchingNextPage || !scrollableContainer,
    onChange: inView => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
  })

  return (
    <UpdateCenterCard>
      <div className={"flex flex-col gap-4 px-2 py-6 md:flex-row md:items-center md:justify-between"}>
        <h2 className={"text-xl font-bold md:text-2xl"}>{t("SalesAssistant_UpdateCenter_Title")}</h2>
        <div className={"flex items-center gap-2"}>
          <div className={"flex items-center gap-2"}>
            <div ref={calendarRef} className={"relative"}>
              <Button
                onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                variant={"outlined"}
                color={"gray"}
                size={"base"}
                className={`p-2.5`}
              >
                <CalendarDaysIcon className={`h-5 w-5`} />
              </Button>
              {isCalendarOpen && (
                <div
                  className={
                    "fixed top-1/2 left-1/2 z-50 w-[calc(100vw-2rem)] max-w-96 -translate-x-1/2 -translate-y-1/2 card p-4 md:absolute md:top-12 md:right-0 md:left-auto md:w-96 md:translate-x-0 md:translate-y-0"
                  }
                >
                  <Calendar value={selectedDate ?? undefined} onSelect={handleDateSelect} />
                </div>
              )}
            </div>
            {selectedDate && (
              <Button
                onClick={handleClearDate}
                variant={"light"}
                color={"gray"}
                size={"base"}
                className={"bg-white p-2.5"}
                aria-label={t("SalesAssistant_UpdateCenter_ClearDateSelection")}
              >
                <XMarkIcon className={"h-5 w-5 text-gray-700"} />
              </Button>
            )}
          </div>
          <div className={"w-full md:w-64"}>
            <InputBase
              placeholder={t("SearchPlaceholder")}
              Icon={MagnifyingGlassIcon}
              type={"search"}
              value={searchString}
              onChange={e => setSearchString(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div ref={scrollableContainerRef} className={"scrollbar-slim flex-grow overflow-y-auto"}>
        {isFetching && allEntries.length === 0 ? (
          <div className={"flex justify-center py-8 text-gray-500"}>{t("SalesAssistant_UpdateCenter_Loading")}</div>
        ) : allEntries.length === 0 ? (
          <div className={"flex justify-center py-8 text-gray-500"}>
            {t("SalesAssistant_UpdateCenter_NoUpdatesFound")}
          </div>
        ) : (
          <>
            {allEntries.map((entry, index) => {
              const previousEntry = index > 0 ? allEntries[index - 1] : null
              const showDateHeader = !previousEntry || entry.tracked_date !== previousEntry.tracked_date
              return <UpdateCenterEntryCard key={entry.id ?? index} entry={entry} showDateHeader={showDateHeader} />
            })}
          </>
        )}
        {hasNextPage && <div ref={loadMoreRef} aria-hidden={"true"} />}
      </div>
    </UpdateCenterCard>
  )
}

const UpdateCenterCard: React.FC<{children: React.ReactNode}> = ({children}) => {
  return (
    <div
      className={
        "flex max-h-[600px] flex-col card-shadow px-8 pb-6 md:max-h-[600px] lg:absolute lg:inset-0 lg:max-h-none"
      }
    >
      {children}
    </div>
  )
}
